<template>
  <div
    class="base-shadow bg-background-neutral-secondary space-y-4 rounded-2xl p-4"
  >
    <CardHeader
      :flight-idx="flightIdx"
      :source-page="sourcePage"
      :dep="segmentsData.startCity"
      :arr="segmentsData.endCity"
      :duration="flight.durationText"
    />
    <MismatchedCity
      v-if="mismatchedCityNames"
      :mismatched-text="
        t('citiesMismatch.alternativeCity', mismatchedCityNames)
      "
    />
    <div v-for="(segment, idx) in flight.segments" :key="idx" class="space-y-4">
      <div
        v-if="ticketDetails.lib.isRailSegment(segment)"
        class="rounded-md bg-[#1D6BC0]/10 py-1.5 text-center text-xs font-medium text-[#1D6BC0] uppercase"
      >
        {{ t('railSegment') }}
      </div>
      <FlightDescription
        :segment="segment"
        :prev-segment-arr="segmentsData.getPrevSegmentArr(idx)"
        :next-segment-dep="segmentsData.getNextSegmentDep(idx)"
        :segment-idx="idx"
        :flight-idx="flightIdx"
        :source-page="sourcePage"
        :display-luggage="displayLuggage"
        @onNewHandLuggageClick="$emit('onNewHandLuggageClick')"
      />
      <FlightConnection
        v-if="segmentsData.isFlightConnectionVisible(idx)"
        :segment="segment"
        :next-segment="props.flight.segments[idx + 1]"
        :source-page="sourcePage"
        :show-smart-route-rows="showSmartRouteRows"
        :needs-transit-visa="needsTransitVisaAt(idx)"
        @onItemClick="emit('onFaqItemClick', $event)"
        @onNightTransferClick="emit('onNightTransferClick')"
        @onRecheckClick="emit('onRecheckClick')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ticketDetails } from 'shared';
  import type { Dict } from '@aviata/platform-types';

  import messages from '../config/locales.json';
  import { needsTransitVisaAtConnection } from '../lib';
  import { MismatchedCity } from './mismatched-city';
  import { FlightDescription } from './flight-details-description';
  import { FlightConnection } from './flight-connection';
  import CardHeader from './CardHeader.vue';

  const { t } = useI18n({ messages });

  const props = withDefaults(
    defineProps<{
      sourcePage?: string;
      flight: ticketDetails.types.MappedFlight;
      cityMismatches: ticketDetails.types.MismatchedCity[] | null;
      flightIdx: number;
      dict: Dict.Entity | null;
      displayLuggage?: boolean;
      /** Opt the interactive smart-route connection rows into a non-details
       *  consumer (the booking-flow flight-details bottom sheet). */
      showSmartRouteRows?: boolean;
    }>(),
    {
      displayLuggage: true,
    },
  );

  const emit = defineEmits<{
    onFaqItemClick: [itemKey: string];
    onNewHandLuggageClick: [];
    onNightTransferClick: [];
    onRecheckClick: [];
  }>();

  const needsTransitVisaAt = (segmentIdx: number) =>
    props.sourcePage === 'details' &&
    needsTransitVisaAtConnection(props.flight, segmentIdx, props.dict);

  const mismatchedCityNames = computed(() =>
    ticketDetails.lib.getMismatchedCityNames(
      props.dict,
      props.cityMismatches,
      props.flightIdx,
    ),
  );
  const segmentsData = computed(() =>
    ticketDetails.lib.getSegmentsData(props.flight),
  );
</script>
